import { ApiError, getGetSessionQueryKey } from "@workspace/api-client-react";
import type { QueryClient } from "@tanstack/react-query";
import { toast } from "@/hooks/use-toast";

const TRANSIENT_STATUS_CODES = new Set([408, 425, 429, 500, 502, 503, 504]);

export function isTransientApiError(error: unknown): boolean {
  if (error instanceof ApiError) {
    return TRANSIENT_STATUS_CODES.has(error.status);
  }
  if (isNetworkError(error)) {
    return true;
  }
  return false;
}

export function isNetworkError(error: unknown): boolean {
  if (error == null) return false;
  if (error instanceof TypeError) {
    const msg = error.message || "";
    return (
      msg.includes("Failed to fetch") ||
      msg.includes("NetworkError") ||
      msg.includes("Network request failed") ||
      msg.includes("Load failed")
    );
  }
  if (error instanceof DOMException && error.name === "AbortError") {
    return true;
  }
  if (error instanceof Error && error.name === "TimeoutError") {
    return true;
  }
  return false;
}

export function isAuthError(error: unknown): boolean {
  return (
    error instanceof ApiError && (error.status === 401 || error.status === 403)
  );
}

const MAX_QUERY_RETRIES = 3;

export function shouldRetryQuery(
  failureCount: number,
  error: unknown,
): boolean {
  if (failureCount >= MAX_QUERY_RETRIES) return false;
  return isTransientApiError(error);
}

let lastToastAt = 0;
const TOAST_COOLDOWN_MS = 5_000;

export function notifyTransientError(error: unknown): void {
  if (!isTransientApiError(error)) return;
  const now = Date.now();
  if (now - lastToastAt < TOAST_COOLDOWN_MS) return;
  lastToastAt = now;

  const description = isNetworkError(error)
    ? "We couldn't reach the server. Retrying automatically — please check your connection."
    : "The server is temporarily unavailable. Retrying automatically.";

  toast({
    title: "Connection issue",
    description,
    variant: "destructive",
  });
}

// ---------------------------------------------------------------------------
// Task #900 — Session-expired handling.
//
// When any authenticated request comes back 401 (server-side session
// invalidated, cookie cleared, etc.) the FE used to silently keep the
// previously-cached user/session/billing state, leaving the sidebar
// showing a stale "Admin" identity and badges rendering "Unavailable"
// while every subsequent call kept 401-ing. We now treat a 401 from
// any non-public endpoint as authoritative proof that the session is
// gone: wipe the cache, mark the session query as null so
// AuthProvider flips isAuthenticated→false, and let ProtectedRoute
// redirect to /login on the next render.
//
// The session endpoint itself (`/auth/session`) is excluded — it is
// the canonical "are you logged in?" probe and its 401 is the normal
// signed-out signal that useGetSession already handles via its
// `retry: false` config. Login/register/etc. endpoints are also
// excluded so failed login attempts don't bounce the user.
// ---------------------------------------------------------------------------

const AUTH_PUBLIC_PATH_FRAGMENTS = [
  "/auth/session",
  "/auth/login",
  "/auth/register",
  "/auth/logout",
  "/auth/magic-link",
  "/auth/invitation",
  "/auth/forgot-password",
  "/auth/reset-password",
  "/auth/verify-email",
];

const PUBLIC_ROUTE_PATHS = new Set([
  "/login",
  "/register",
  "/waitlist",
  "/magic-link/verify",
  "/verify-email",
  "/verify-email-pending",
  "/forgot-password",
  "/reset-password",
  "/landing",
  "/about",
  "/contact",
  "/privacy",
  "/terms",
]);

function isSessionLikelyExpiredError(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  if (error.status !== 401) return false;
  const url = error.url || "";
  return !AUTH_PUBLIC_PATH_FRAGMENTS.some((frag) => url.includes(frag));
}

function isOnPublicRoute(): boolean {
  if (typeof window === "undefined") return false;
  const base = (() => {
    try {
      const v = (import.meta as unknown as { env?: { BASE_URL?: string } }).env
        ?.BASE_URL;
      return typeof v === "string" && v.length > 1
        ? v.replace(/\/+$/, "")
        : "";
    } catch {
      return "";
    }
  })();
  let path = window.location.pathname;
  if (base && path.startsWith(base)) path = path.slice(base.length) || "/";
  if (path === "/") return true;
  for (const pub of PUBLIC_ROUTE_PATHS) {
    if (path === pub || path.startsWith(`${pub}/`)) return true;
  }
  return false;
}

let sessionExpiryRedirectedFor: string | null = null;

/**
 * Handle a 401 from an authenticated request: clear all per-user
 * cache state, mark the session as signed-out so AuthProvider
 * propagates the change immediately, and steer the user to /login
 * (with the current path preserved in `?next=` so they land back
 * where they were after signing in). Idempotent — repeated 401s
 * within the same tick are coalesced.
 */
export function handleSessionExpired(
  queryClient: QueryClient,
  error: unknown,
): boolean {
  if (!isSessionLikelyExpiredError(error)) return false;

  try {
    queryClient.cancelQueries().catch(() => {});
    queryClient.clear();
    queryClient.setQueryData(getGetSessionQueryKey(), null);
  } catch (clearErr) {
    console.warn("[auth] failed to clear query cache on session expiry", clearErr);
  }

  if (typeof window !== "undefined" && !isOnPublicRoute()) {
    try {
      const base = (() => {
        try {
          const v = (import.meta as unknown as { env?: { BASE_URL?: string } })
            .env?.BASE_URL;
          return typeof v === "string" && v.length > 0 ? v : "/";
        } catch {
          return "/";
        }
      })();
      const here =
        window.location.pathname + window.location.search + window.location.hash;
      // Strip the BASE prefix so the `next` param is a pure app-relative path.
      const baseClean = base.replace(/\/+$/, "");
      const hereAppRelative =
        baseClean && here.startsWith(baseClean) ? here.slice(baseClean.length) : here;
      const next =
        hereAppRelative && hereAppRelative !== "/login"
          ? `?next=${encodeURIComponent(hereAppRelative)}`
          : "";
      const loginUrl = `${base.replace(/\/+$/, "/")}login${next}`;
      // Coalesce repeated 401s in the same burst to a single assign() —
      // multiple navigations to the same URL are a noop in browsers but
      // some test environments treat the second call as flaky.
      if (sessionExpiryRedirectedFor !== loginUrl) {
        sessionExpiryRedirectedFor = loginUrl;
        window.location.assign(loginUrl);
      }
    } catch (navErr) {
      console.warn("[auth] failed to redirect to login on session expiry", navErr);
    }
  }

  return true;
}

// Test-only — reset the redirect coalescing memo between tests.
export function _resetSessionExpiryStateForTests(): void {
  sessionExpiryRedirectedFor = null;
}

let unhandledRejectionListenerInstalled = false;

export function installUnhandledRejectionGuard(): void {
  if (unhandledRejectionListenerInstalled) return;
  if (typeof window === "undefined") return;
  unhandledRejectionListenerInstalled = true;

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    if (reason instanceof ApiError || isNetworkError(reason)) {
      try {
        notifyTransientError(reason);
      } catch {
        /* ignore toast failures */
      }
      console.warn(
        "[api] swallowed unhandled API rejection:",
        reason instanceof Error ? reason.message : reason,
      );
      event.preventDefault();
    }
  });
}
